// frontend/src/utils/practitionerUtils.js
import { normalizeTypeForUi, safeText } from "@utils/utils";
import { TYPE_ORDER } from "@utils/practitionerTypes"; // shared sort order for all 16 types

export function getRoster(data) {
  if (!data) return [];
  if (Array.isArray(data)) return data;
  if (Array.isArray(data.results)) return data.results;
  return [];
}

// Returns true when a practitioner's type matches the active filter.
// Simple equality check: "ALL" passes everything; any other value (e.g. "PL", "HYG")
// must match exactly. The old implementation had a fallback `return true` which meant
// unknown filter values accidentally showed all practitioners instead of filtering.
export function matchesFilter(typeUi, filter) {
  if (filter === "ALL") return true;
  return typeUi === filter;
}

/** UI type string ("DDS" | "HYG" | …) from a practitioner row. */
export function getUiTypeFromPractitioner(p) {
  if (!p) return "";
  return normalizeTypeForUi(p.practitioner_type);
}

// ─── TEMPORARY HOLD (manager request) ────────────────────────────────────────
// The newer practitioner types (Area Manager, Practitioner Lead, Receptionist,
// Surgeon, Specialist, Dental Assistant, etc.) are intentionally shown ONLY under
// Employee Profile and Schedule/Vacation for now. Every other module — Daily
// Financial, Monthly Reconciliation, Journal Entry, Associate Fee, Deposit Sheet,
// Lab Invoices, Forecasting, etc. — must keep behaving exactly as before and show
// ONLY Dentists and Hygienists. To lift the hold and support all types everywhere,
// delete FINANCIAL_MODULE_UI_TYPES + filterToFinancialTypes and their call sites.
export const FINANCIAL_MODULE_UI_TYPES = ["DDS", "HYG"];

/** True when a practitioner is a Dentist or Hygienist (the only types the financial
 *  modules handle). Verified safe: new-type practitioners have no reports/compensation. */
export function isFinancialModuleType(p) {
  return FINANCIAL_MODULE_UI_TYPES.includes(getUiTypeFromPractitioner(p));
}

/** Filter a practitioner list down to Dentists + Hygienists (financial modules only). */
export function filterToFinancialTypes(list) {
  return (Array.isArray(list) ? list : []).filter(isFinancialModuleType);
}

export function toTime(v) {
  if (!v) return 0;
  const t = Date.parse(v);
  return Number.isFinite(t) ? t : 0;
}

/**
 * ✅ newest -> oldest (recently created on top)
 * - Prefer created_at/createdAt/created
 * - Fallback to id DESC
 */
export function sortNewestFirst(list) {
  const copy = list.slice();
  copy.sort((a, b) => {
    const aT = toTime(a?.created_at || a?.createdAt || a?.created);
    const bT = toTime(b?.created_at || b?.createdAt || b?.created);

    if (aT !== bT) return bT - aT;

    const aId = Number(a?.id) || 0;
    const bId = Number(b?.id) || 0;
    return bId - aId;
  });
  return copy;
}

// Updated to use shared TYPE_ORDER instead of a hardcoded 3-entry map (DDS=0, HYG=1, other=2).
// Now correctly sorts all 16 types, not just dentists and hygienists.
export function sortPractitionersDdsThenHyg(list) {
  const copy = list.slice();
  copy.sort((a, b) => {
    const tA = TYPE_ORDER[normalizeTypeForUi(a?.practitioner_type)] ?? 99;
    const tB = TYPE_ORDER[normalizeTypeForUi(b?.practitioner_type)] ?? 99;
    return tA - tB;
  });
  return copy;
}

// New function — replaces per-component inline sort comparators in SchedulePage and NewShiftPage.
/**
 * Sort comparator: by TYPE_ORDER ascending, then name alphabetically.
 * Pass directly to Array.sort — e.g. roster.slice().sort(compareByTypeAndName).
 */
export function compareByTypeAndName(a, b) {
  const tA = TYPE_ORDER[normalizeTypeForUi(a?.practitioner_type)] ?? 99;
  const tB = TYPE_ORDER[normalizeTypeForUi(b?.practitioner_type)] ?? 99;
  if (tA !== tB) return tA - tB;
  return safeText(a?.name).localeCompare(safeText(b?.name));
}

// New function — previously duplicated inline in NewShiftPage, SchedulePage, and ClinicCalendarTab.
/** Strips "Dr." prefix and returns up to 2 uppercase initials, falling back to "?". */
export function getInitials(name) {
  return (
    safeText(name)
      .replace(/^dr\.?\s*/i, "")
      .trim()
      .split(/\s+/)
      .slice(0, 2)
      .map((w) => w[0]?.toUpperCase())
      .join("") || "?"
  );
}
